import React, { useState } from 'react'
import { Select } from '../shared/components'
import { useCarFipe } from '../hooks/useFipe'
import { FipePrice } from '../types/fipe'

const FipeDemo: React.FC = () => {
  const { 
    brands, 
    models, 
    years, 
    loading, 
    error, 
    selectedBrand, 
    selectedModel, 
    selectedYear, 
    setSelectedBrand, 
    setSelectedModel, 
    setSelectedYear,
    getPrice 
  } = useCarFipe()

  const [priceData, setPriceData] = useState<FipePrice | null>(null)
  const [priceLoading, setPriceLoading] = useState(false)
  const [priceError, setPriceError] = useState<string | null>(null)

  const handleGetPrice = async () => {
    setPriceLoading(true)
    setPriceError(null)
    setPriceData(null)

    try {
      const price = await getPrice()
      if (price) {
        setPriceData(price)
      }
    } catch (err) {
      setPriceError('Erro ao consultar preço FIPE')
    } finally {
      setPriceLoading(false)
    }
  }

  const formatPrice = (value: string) => {
    // Remove caracteres não numéricos e formata como moeda
    const numericValue = value.replace(/[^\d]/g, '')
    if (numericValue) {
      const price = parseInt(numericValue)
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(price)
    }
    return value
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-sm">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🚗 Demonstração API FIPE
        </h2>
        <p className="text-gray-600">
          Teste a integração com a API FIPE para consultar preços de veículos
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Marca */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marca do Veículo
          </label>
          <Select
            value={selectedBrand?.codigo || ''}
            onChange={(value) => {
              const brand = brands.find(b => b.codigo === value)
              setSelectedBrand(brand || null)
            }}
            options={[
              { value: '', label: 'Selecione a marca' },
              ...brands.map(brand => ({
                value: brand.codigo,
                label: brand.nome
              }))
            ]}
            placeholder="Selecione a marca"
            disabled={loading}
            className="w-full"
          />
        </div>

        {/* Modelo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Modelo
          </label>
          <Select
            value={selectedModel?.codigo || ''}
            onChange={(value) => {
              const model = models.find(m => m.codigo === value)
              setSelectedModel(model || null)
            }}
            options={[
              { value: '', label: 'Selecione o modelo' },
              ...models.map(model => ({
                value: model.codigo,
                label: model.nome
              }))
            ]}
            placeholder="Selecione o modelo"
            disabled={!selectedBrand || loading}
            className="w-full"
          />
        </div>

        {/* Ano */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ano
          </label>
          <Select
            value={selectedYear?.codigo || ''}
            onChange={(value) => {
              const year = years.find(y => y.codigo === value)
              setSelectedYear(year || null)
            }}
            options={[
              { value: '', label: 'Selecione o ano' },
              ...years.map(year => ({
                value: year.codigo,
                label: year.nome
              }))
            ]}
            placeholder="Selecione o ano"
            disabled={!selectedModel || loading}
            className="w-full"
          />
        </div>
      </div>

      {/* Botão de consulta */}
      <div className="mb-6">
        <button
          onClick={handleGetPrice}
          disabled={!selectedBrand || !selectedModel || !selectedYear || loading || priceLoading}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {priceLoading ? 'Consultando...' : 'Consultar Preço FIPE'}
        </button>
      </div>

      {/* Resultado */}
      {priceError && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {priceError}
        </div>
      )}

      {priceData && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 mb-4">
            📊 Preço FIPE Consultado
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-green-700 mb-1">Veículo:</p>
              <p className="font-medium text-green-900">
                {priceData.Marca} {priceData.Modelo}
              </p>
            </div>
            <div>
              <p className="text-sm text-green-700 mb-1">Ano:</p>
              <p className="font-medium text-green-900">{priceData.AnoModelo}</p>
            </div>
            <div>
              <p className="text-sm text-green-700 mb-1">Combustível:</p>
              <p className="font-medium text-green-900">{priceData.Combustivel}</p>
            </div>
            <div>
              <p className="text-sm text-green-700 mb-1">Código FIPE:</p>
              <p className="font-medium text-green-900">{priceData.CodigoFipe}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-green-700 mb-1">Valor:</p>
              <p className="text-2xl font-bold text-green-900">
                {formatPrice(priceData.Valor)}
              </p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-green-700 mb-1">Referência:</p>
              <p className="font-medium text-green-900">{priceData.MesReferencia}</p>
            </div>
          </div>
        </div>
      )}

      {/* Informações sobre a API */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">
          ℹ️ Sobre a API FIPE
        </h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Dados atualizados mensalmente pela FIPE</li>
          <li>• Limite de 500 requisições gratuitas por dia</li>
          <li>• Cache local de 24 horas para melhor performance</li>
          <li>• Suporte a carros, motos e caminhões</li>
        </ul>
      </div>
    </div>
  )
}

export default FipeDemo
